// @ts-strict-ignore

import { Skeleton } from "@fullscript/aviary-web";
import type { ReactNode } from "react";

import { Column, Columns } from "@aviary";
import { useStoreConfigQuery } from "@shared/data/queries/StoreConfigQuery/StoreConfig.query.generated";
import type { StoreConfigType } from "@shared/data/queries/StoreConfigQuery/StoreConfig.types";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { useQualityStorySignupExperiment } from "@shared/hooks/useQualityStorySignupExperiment/useQualityStorySignupExperiment";
import { SideCarouselPanel } from "@unauthenticated/shared/components/SideCarouselPanel/SideCarouselPanel";
import { FullBleedShell } from "@unauthenticated/signupSignin/components/FullBleedShell/FullBleedShell";
import * as fullBleedStyles from "@unauthenticated/signupSignin/components/FullBleedShell/FullBleedShell.styles";
import { Page } from "@unauthenticated/signupSignin/components/Page/Page";
import { PatientInfoBlock } from "@unauthenticated/signupSignin/components/PatientInfoBlock/PatientInfoBlock";
import { StorefrontWelcomeLogo } from "@unauthenticated/signupSignin/components/StorefrontWelcomeLogo/StorefrontWelcomeLogo";
import { StoreLandingFooter } from "@unauthenticated/signupSignin/components/StoreLandingFooter/StoreLandingFooter";
import { StoreLandingPageNavBar } from "@unauthenticated/signupSignin/components/StoreLandingPageNavBar/StoreLandingPageNavBar";
import { StoreSignupInCarousel } from "@unauthenticated/signupSignin/components/StoreSignupInCarousel/StoreSignupInCarousel";

import { PromotionBanner, type PromotionBannerProps } from "./PromotionBanner/PromotionBanner";
import { useWelcomeCarouselSlides } from "./useWelcomeCarouselSlides/useWelcomeCarouselSlides";

import * as styles from "./StoreLandingLayout.styles";

type SceneRenderArgs = {
  storefrontConfig: StoreConfigType | null;
  isFullBleed: boolean;
};

interface Props extends Partial<PromotionBannerProps> {
  children: ReactNode | ((sceneRenderArgs: SceneRenderArgs) => ReactNode);
  storeName: string;
  storeSlug?: string;
  pracAvatar?: string;
  isPreview?: boolean;
  isStoreOwner?: boolean;
  storeId?: string;
  isStorefronts?: boolean;
  organizationLogo?: string;
  iconLogo?: string;
  showDeliveryCountry?: boolean;
  hasFullBleedPanel?: boolean;
}

const StoreLandingLayout = ({
  children,
  storeName,
  storeSlug,
  pracAvatar,
  isPreview,
  isStoreOwner,
  isActivePromotion,
  storeId,
  isStorefronts,
  organizationLogo,
  iconLogo,
  showDeliveryCountry,
  hasFullBleedPanel,
}: Props) => {
  const { phoneLarge, tablet } = useBreakpoints();
  const isQualityStorySignupEnabled = useQualityStorySignupExperiment({ shouldLog: false });

  const { data: storeConfigData, loading: storeConfigLoading } = useStoreConfigQuery({
    variables: { storeSlug },
    skip: !storeSlug,
  });

  const storefrontConfig = storeConfigData?.viewer?.storefrontConfig;
  const isFullBleed =
    !!hasFullBleedPanel &&
    !!isQualityStorySignupEnabled &&
    !isStorefronts &&
    !!phoneLarge.greaterThan &&
    (storeConfigLoading || !storefrontConfig?.customAuthPageMarketingCopy);
  const isStoryPanelVisible = isFullBleed && !!tablet.greaterThan;

  const welcomeCarouselSlides = useWelcomeCarouselSlides({
    isCarouselVisible: isStoryPanelVisible,
    isActivePromotion,
    storeId,
  });

  const renderStoryPanel = () => {
    if (!isStoryPanelVisible) return null;

    return storeConfigLoading ? (
      <Skeleton customCss={fullBleedStyles.panelPlaceholder} />
    ) : (
      <SideCarouselPanel slides={welcomeCarouselSlides} />
    );
  };

  const renderLeftColumnBlock = () => {
    if (isStorefronts) {
      return <StorefrontWelcomeLogo organizationLogo={organizationLogo} storeName={storeName} />;
    }

    return (
      <PatientInfoBlock
        storeName={storeName}
        pracAvatar={pracAvatar}
        customMarketing={storefrontConfig?.customAuthPageMarketingCopy}
        loading={storeConfigLoading}
      >
        <StoreSignupInCarousel storeName={storeName} />
        <PromotionBanner
          isActivePromotion={isActivePromotion}
          storeId={storeId}
          storeName={storeName}
        />
      </PatientInfoBlock>
    );
  };

  const renderSceneContent = () =>
    typeof children === "function"
      ? children({ storefrontConfig: storefrontConfig ?? null, isFullBleed })
      : children;

  const renderLayoutBody = () => {
    if (isFullBleed) {
      return (
        <FullBleedShell panel={renderStoryPanel()}>
          <div css={styles.fullBleedContent}>{renderSceneContent()}</div>
        </FullBleedShell>
      );
    }

    return (
      <Columns css={styles.columnsContainer}>
        {!!phoneLarge.greaterThan && (
          <Column columnWidth={6} css={styles.leftColumn}>
            {renderLeftColumnBlock()}
          </Column>
        )}
        <Column columnWidth={6} css={styles.rightColumn}>
          {renderSceneContent()}
        </Column>
      </Columns>
    );
  };

  return (
    <Page>
      <StoreLandingPageNavBar
        isPreview={isPreview}
        isStoreOwner={isStoreOwner}
        isStorefronts={isStorefronts}
        iconLogo={iconLogo}
      />
      <div css={styles.layoutContainer}>{renderLayoutBody()}</div>
      <StoreLandingFooter showDeliveryCountry={!!showDeliveryCountry && !isStorefronts} />
    </Page>
  );
};

export { StoreLandingLayout };
